import { ApolloQueryResult, NetworkStatus, gql, useMutation, useQuery } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';
import Fieldset from 'components/Fieldset';
import DateInput from 'components/Inputs/Date';
import { Select } from 'components/Inputs/Select';
import Navbar from 'components/Navbar';
import { ConfirmationPopup } from 'components/PopUp';
import { useUser } from 'contexts/userContext';
import dayjs, { Dayjs } from 'dayjs';
import { MELT_USERS, MeltUsersResponse, MeltUsersVariables } from 'graphql/mutation/user';
import {
  GET_FREEZED_USERS,
  GetFreezedUsersResponse,
  GetFreezedUsersVariables,
} from 'graphql/query/user';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { YearSettings } from 'types';
import { getSelectedObjectiveGoalsYear } from 'utils/common';

const GET_YEAR_SETTINGS = gql`
  query GetYearSettings($year: Int) {
    getYearSettings(year: $year) {
      _id
      minute
      hour
      goalSettingStartDay
      goalSettingStartMonth
      goalSettingEndDay
      goalSettingEndMonth
      goalSettingReminderDays
      midYearStartDay
      midYearStartMonth
      midYearEndDay
      midYearEndMonth
      midYearReminderDays
      endYearStartDay
      endYearStartMonth
      endYearEndDay
      endYearEndMonth
      endYearReminderDays
      year
    }
  }
`;

type GetYearSettingsResponse = {
  getYearSettings: YearSettings;
};

type GetYearSettingsVariables = {
  year: number;
};

const SET_YEAR_SETTINGS = gql`
  mutation SetYearSettings($input: YearSettingInput) {
    setYearSettings(input: $input) {
      _id
      minute
      hour
      goalSettingStartDay
      goalSettingStartMonth
      goalSettingEndDay
      goalSettingEndMonth
      goalSettingReminderDays
      midYearStartDay
      midYearStartMonth
      midYearEndDay
      midYearEndMonth
      midYearReminderDays
      endYearStartDay
      endYearStartMonth
      endYearEndDay
      endYearEndMonth
      endYearReminderDays
      year
    }
  }
`;

type SetYearSettingsResponse = {
  setYearSettings: YearSettings;
};

type SetYearSettingsVariables = {
  input: {
    _id?: string;
    startDate: string;
    endDate: string;
    period: number;
    reminderDays?: number;
  };
};

const formDate = (day: number, month: number, year: string) => {
  return dayjs(`${month}/${day}/${year}`);
};

const ObjectiveYearSettings = () => {
  const { user } = useUser();

  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(
    getSelectedObjectiveGoalsYear().toString() ?? currentYear
  );

  const userCreatedYear = new Date(user?.createdAt!).getFullYear();
  const isAdmin = user?.role.name === 'ADMIN';

  const {
    data: currYearSettings,
    networkStatus,
    refetch: refetchYearSettings,
  } = useQuery<GetYearSettingsResponse, GetYearSettingsVariables>(GET_YEAR_SETTINGS, {
    variables: {
      year: +selectedYear,
    },
    notifyOnNetworkStatusChange: true,
  });

  const loadingYearSettings =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    !!!currYearSettings;

  const options = useMemo(() => {
    const years: number[] = [];
    for (let i = 0; i <= currentYear - userCreatedYear; i++) {
      years.push(userCreatedYear + i);
    }

    return years.map(yr => ({ label: yr.toString(), value: yr.toString() }));
  }, [userCreatedYear]);

  return (
    <Navbar title="Objective Year Settings">
      <Grid container columnSpacing={2} rowGap={2}>
        <Grid item xs={12} md={7} mt={2}>
          <Select
            label="Select Year"
            options={options}
            value={selectedYear}
            onChange={val => {
              setSelectedYear(val);
              toast.success('Year selected successfully!');
              localStorage.setItem('selectedObjectiveSettingYear', val);
            }}
          />
        </Grid>

        {isAdmin && (
          <>
            {loadingYearSettings ? (
              <LoadingButton />
            ) : (
              <YearSettingForms
                currYearSettings={currYearSettings.getYearSettings}
                year={selectedYear}
                refetchYearSettings={refetchYearSettings}
              />
            )}
          </>
        )}
      </Grid>
    </Navbar>
  );
};

type FormType = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

const YearSettingForms: React.FC<{
  currYearSettings: YearSettings;
  year: string;
  refetchYearSettings: (
    variables?: Partial<GetYearSettingsVariables> | undefined
  ) => Promise<ApolloQueryResult<GetYearSettingsResponse>>;
}> = ({ currYearSettings, year, refetchYearSettings }) => {
  const [startYearFormState, setStartYearFormState] = useState<FormType>(
    !!!currYearSettings.goalSettingStartDay
      ? {
          startDate: null,
          endDate: null,
        }
      : {
          startDate: formDate(
            currYearSettings.goalSettingStartDay,
            currYearSettings.goalSettingStartMonth,
            year
          ),
          endDate: formDate(
            currYearSettings.goalSettingEndDay,
            currYearSettings.goalSettingEndMonth,
            year
          ),
        }
  );
  const [midYearFormState, setMidYearFormState] = useState<FormType>(
    !!!currYearSettings.midYearStartDay
      ? {
          startDate: null,
          endDate: null,
        }
      : {
          startDate: formDate(
            currYearSettings.midYearStartDay,
            currYearSettings.midYearStartMonth,
            year
          ),
          endDate: formDate(currYearSettings.midYearEndDay, currYearSettings.midYearEndMonth, year),
        }
  );
  const [endYearFormState, setEndYearFormState] = useState<FormType>(
    !!!currYearSettings.endYearStartDay
      ? {
          startDate: null,
          endDate: null,
        }
      : {
          startDate: formDate(
            currYearSettings.endYearStartDay,
            currYearSettings.endYearStartMonth,
            year
          ),
          endDate: formDate(currYearSettings.endYearEndDay, currYearSettings.endYearEndMonth, year),
        }
  );

  const [startYearPopup, toggleStartYearPopup] = useState(false);
  const [midYearPopup, toggleMidYearPopup] = useState(false);
  const [endYearPopup, toggleEndYearPopup] = useState(false);

  const currentYear = new Date().getFullYear();
  const selectedYear = getSelectedObjectiveGoalsYear();
  const isRunningYear = selectedYear === currentYear;

  const { data: startYearFreezedUsers, refetch: refetchStartYearFreezedUsers } = useQuery<
    GetFreezedUsersResponse,
    GetFreezedUsersVariables
  >(GET_FREEZED_USERS, { variables: { period: 1, year: selectedYear } });

  const [startYearMeltUsers, { loading: meltingStartYearUsers }] = useMutation<
    MeltUsersResponse,
    MeltUsersVariables
  >(MELT_USERS);

  const { data: midYearFreezedUsers, refetch: refetchMidYearFreezedUsers } = useQuery<
    GetFreezedUsersResponse,
    GetFreezedUsersVariables
  >(GET_FREEZED_USERS, { variables: { period: 2, year: selectedYear } });

  const [midYearMeltUsers, { loading: meltingMidYearUsers }] = useMutation<
    MeltUsersResponse,
    MeltUsersVariables
  >(MELT_USERS);

  const { data: endYearFreezedUsers, refetch: refetchEndYearFreezedUsers } = useQuery<
    GetFreezedUsersResponse,
    GetFreezedUsersVariables
  >(GET_FREEZED_USERS, { variables: { period: 3, year: selectedYear } });

  const [endYearMeltUsers, { loading: meltingEndYearUsers }] = useMutation<
    MeltUsersResponse,
    MeltUsersVariables
  >(MELT_USERS);

  const [setYearSettings, { loading: settingYearSettings }] = useMutation<
    SetYearSettingsResponse,
    SetYearSettingsVariables
  >(SET_YEAR_SETTINGS);

  const handleStartYearChange = (fieldName: string, value: Dayjs | null) => {
    setStartYearFormState(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleMidYearChange = (fieldName: string, value: Dayjs | null) => {
    setMidYearFormState(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleEndYearChange = (fieldName: string, value: Dayjs | null) => {
    setEndYearFormState(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleStartYearSubmit = () => {
    setYearSettings({
      variables: {
        input: {
          startDate: dayjs(startYearFormState.startDate!).format('DD/MM/YYYY'),
          endDate: dayjs(startYearFormState.endDate!).format('DD/MM/YYYY'),
          period: 1,
        },
      },
      onCompleted: _ => {
        toast.success('Objective goals timeline created successfully.');
        refetchYearSettings({
          year: +year,
        });
        toggleStartYearPopup(false);
      },
    });
  };

  const handleMidYearSubmit = () => {
    setYearSettings({
      variables: {
        input: {
          _id: currYearSettings._id,
          startDate: dayjs(midYearFormState.startDate!).format('DD/MM/YYYY'),
          endDate: dayjs(midYearFormState.endDate!).format('DD/MM/YYYY'),
          period: 2,
        },
      },
      onCompleted: _ => {
        toast.success(`Mid Year review's timeline created successfully.`);
        toggleMidYearPopup(false);
      },
    });
  };

  const handleEndYearSubmit = () => {
    setYearSettings({
      variables: {
        input: {
          _id: currYearSettings._id,
          startDate: dayjs(endYearFormState.startDate!).format('DD/MM/YYYY'),
          endDate: dayjs(endYearFormState.endDate!).format('DD/MM/YYYY'),
          period: 3,
        },
      },
      onCompleted: _ => {
        toast.success(`End Year review's timeline created successfully.`);
        toggleEndYearPopup(false);
      },
    });
  };

  return (
    <>
      <Grid item xs={12} md={7}>
        <Fieldset label="Objective Settings">
          <form
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            onSubmit={e => {
              e.preventDefault();
              toggleStartYearPopup(true);
            }}
          >
            <Grid
              container
              item
              xs={12}
              direction={'row'}
              justifyContent={'space-between'}
              columnSpacing={1}
              rowGap={2}
            >
              <Grid item xs={12} md={6}>
                <DateInput
                  value={startYearFormState.startDate}
                  onChange={val => handleStartYearChange('startDate', val)}
                  label="Start Date"
                  required
                  disablePast={!(!!currYearSettings.goalSettingStartDay || !isRunningYear)}
                  disabled={!!currYearSettings.goalSettingStartDay || !isRunningYear}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateInput
                  value={startYearFormState.endDate}
                  onChange={val => handleStartYearChange('endDate', val)}
                  label="End Date"
                  required
                  disablePast={
                    !(!!!startYearFormState.startDate || !!currYearSettings.goalSettingStartDay)
                  }
                  minDate={
                    !!startYearFormState.startDate ? dayjs(startYearFormState.startDate) : undefined
                  }
                  disabled={
                    !!!startYearFormState.startDate || !!currYearSettings.goalSettingStartDay
                  }
                />
              </Grid>

              <Grid container item xs={12} justifyContent={'flex-end'} columnGap={1}>
                <LoadingButton
                  variant="contained"
                  loading={meltingStartYearUsers}
                  disabled={!isRunningYear || !!!startYearFreezedUsers?.getFreezedUsers}
                  onClick={() =>
                    startYearMeltUsers({
                      variables: { period: 1, year: selectedYear },
                      onCompleted: () => {
                        refetchStartYearFreezedUsers();
                      },
                    })
                  }
                >
                  Unfreeze
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  disabled={!!currYearSettings.goalSettingStartDay || !isRunningYear}
                  loading={!!!currYearSettings.goalSettingStartDay && settingYearSettings}
                >
                  Submit
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </Fieldset>
      </Grid>

      {!!currYearSettings.goalSettingStartDay && (
        <Grid item xs={12} md={7}>
          <Fieldset label="Mid year Settings">
            <form
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              onSubmit={e => {
                e.preventDefault();
                toggleMidYearPopup(true);
              }}
            >
              <Grid
                container
                item
                xs={12}
                direction={'row'}
                justifyContent={'space-between'}
                columnSpacing={1}
                rowGap={2}
              >
                <Grid item xs={12} md={6}>
                  <DateInput
                    value={midYearFormState.startDate}
                    onChange={val => handleMidYearChange('startDate', val)}
                    label="Start Date"
                    required
                    disablePast={!!!currYearSettings.midYearStartDay}
                    disabled={!!currYearSettings.midYearStartDay}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateInput
                    value={midYearFormState.endDate}
                    onChange={val => handleMidYearChange('endDate', val)}
                    label="End Date"
                    required
                    disablePast={
                      !(!!!midYearFormState.startDate || !!currYearSettings.midYearStartDay)
                    }
                    minDate={
                      !!midYearFormState.startDate ? dayjs(midYearFormState.startDate) : undefined
                    }
                    disabled={!!!midYearFormState.startDate || !!currYearSettings.midYearStartDay}
                  />
                </Grid>

                <Grid container item xs={12} justifyContent={'flex-end'} columnGap={1}>
                  <LoadingButton
                    variant="contained"
                    loading={meltingMidYearUsers}
                    disabled={!isRunningYear || !!!midYearFreezedUsers?.getFreezedUsers}
                    onClick={() =>
                      midYearMeltUsers({
                        variables: { period: 2, year: selectedYear },
                        onCompleted: () => {
                          refetchMidYearFreezedUsers();
                        },
                      })
                    }
                  >
                    Unfreeze
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    disabled={!!currYearSettings.midYearStartDay}
                    loading={!!!currYearSettings.midYearStartDay && settingYearSettings}
                  >
                    Submit
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </Fieldset>
        </Grid>
      )}

      {!!currYearSettings.midYearStartDay && (
        <Grid item xs={12} md={7}>
          <Fieldset label="End year Settings">
            <form
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              onSubmit={e => {
                e.preventDefault();
                toggleEndYearPopup(true);
              }}
            >
              <Grid
                container
                item
                xs={12}
                direction={'row'}
                justifyContent={'space-between'}
                columnSpacing={1}
                rowGap={2}
              >
                <Grid item xs={12} md={6}>
                  <DateInput
                    value={endYearFormState.startDate}
                    onChange={val => handleEndYearChange('startDate', val)}
                    label="Start Date"
                    required
                    disablePast={!!!currYearSettings.endYearStartDay}
                    disabled={!!currYearSettings.endYearStartDay}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateInput
                    value={endYearFormState.endDate}
                    onChange={val => handleEndYearChange('endDate', val)}
                    label="End Date"
                    required
                    disablePast={
                      !(!!!endYearFormState.startDate || !!currYearSettings.endYearStartDay)
                    }
                    minDate={
                      !!endYearFormState.startDate ? dayjs(endYearFormState.startDate) : undefined
                    }
                    disabled={!!!endYearFormState.startDate || !!currYearSettings.endYearStartDay}
                  />
                </Grid>

                <Grid container item xs={12} justifyContent={'flex-end'} columnGap={1}>
                  <LoadingButton
                    variant="contained"
                    loading={meltingEndYearUsers}
                    disabled={!isRunningYear || !!!endYearFreezedUsers?.getFreezedUsers}
                    onClick={() =>
                      endYearMeltUsers({
                        variables: { period: 3, year: selectedYear },
                        onCompleted: () => {
                          refetchEndYearFreezedUsers();
                        },
                      })
                    }
                  >
                    Unfreeze
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    disabled={!!currYearSettings.endYearStartDay}
                    type="submit"
                    loading={!!!currYearSettings.endYearStartDay && settingYearSettings}
                  >
                    Submit
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </Fieldset>
        </Grid>
      )}
      <ConfirmationPopup
        title="Are you sure? Once this timeline is submitted it cannot be changed"
        open={startYearPopup}
        onClose={() => toggleStartYearPopup(false)}
        onConfirmation={handleStartYearSubmit}
        loading={settingYearSettings}
        fullWidth
      />
      <ConfirmationPopup
        title="Are you sure? Once this timeline is submitted it cannot be changed"
        open={midYearPopup}
        onClose={() => toggleMidYearPopup(false)}
        onConfirmation={handleMidYearSubmit}
        loading={settingYearSettings}
        fullWidth
      />
      <ConfirmationPopup
        title="Are you sure? Once this timeline is submitted it cannot be changed"
        open={endYearPopup}
        onClose={() => toggleEndYearPopup(false)}
        onConfirmation={handleEndYearSubmit}
        loading={settingYearSettings}
        fullWidth
      />
    </>
  );
};

export default ObjectiveYearSettings;
